// FE-SCRM-055 — Settings → Sales playbooks section.
//
// Table of every active playbook on the entity. Producers can:
// • Star (pin) one as the default selection for FE-SCRM-056's
//   ApplyPlaybookModal — only one row carries the star at a time.
// • Click a row to open a side panel with its activity blocks
//   (read-only; custom playbook authoring is v1.5).
//
// Pinned default storage: a UUID on ``BrokerSettings.pinned_default
// _playbook_id`` (FE-SCRM-055 schema). Toggling the pin PATCHes the
// existing ``/api/v1/entities/<id>/broker-settings`` route so the
// FE has one source of truth for per-entity flags.

import { useState } from "react";

import {
  Button, Table, THead, TBody, Tr, Th, Td,
} from "@/design-system";
import {
  usePlaybookDetailQuery,
  usePlaybooksQuery,
} from "../Playbook/hooks";
import type { PlaybookSummary } from "../Playbook/types";


interface Props {
  pinnedPlaybookId: string | null;
  onPin: (playbookId: string | null) => void;
  patchPending?: boolean;
}


export function PlaybooksSection({
  pinnedPlaybookId, onPin, patchPending = false,
}: Props): JSX.Element {
  const list = usePlaybooksQuery();
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = list.data?.rows ?? [];

  return (
    <section
      data-testid="settings-playbooks"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Sales playbooks
        </h2>
        <p className="text-xs text-text-secondary">
          Star one playbook to make it the default selection when
          producers open the Apply playbook modal on an Opportunity.
        </p>
      </header>

      {list.isLoading ? (
        <p className="text-xs text-text-tertiary">Loading playbooks…</p>
      ) : null}

      {!list.isLoading && rows.length === 0 ? (
        <p
          data-testid="settings-playbooks-empty"
          className="text-xs text-text-tertiary"
        >
          No active playbooks. The seeded library lands automatically;
          contact support if it&rsquo;s missing.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <Table
          layout="fixed"
          cellPadding="tight"
          data-testid="settings-playbooks-table"
          className="border-collapse"
        >
          <THead>
            <Tr className="border-b border-border-subtle text-left text-[10px] uppercase tracking-wide text-text-tertiary">
              <Th className="w-10">Pin</Th>
              <Th>Playbook</Th>
              <Th className="w-32">Type</Th>
              <Th className="w-32">Blocks</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <PlaybookRow
                key={row.playbook_id}
                row={row}
                pinned={row.playbook_id === pinnedPlaybookId}
                onPin={() =>
                  onPin(
                    row.playbook_id === pinnedPlaybookId
                      ? null
                      : row.playbook_id,
                  )}
                onOpen={() => setOpenId(row.playbook_id)}
                patchPending={patchPending}
              />
            ))}
          </TBody>
        </Table>
      ) : null}

      {openId ? (
        <PlaybookBlocksPanel
          playbookId={openId}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </section>
  );
}


function PlaybookRow({
  row, pinned, onPin, onOpen, patchPending,
}: {
  row: PlaybookSummary;
  pinned: boolean;
  onPin: () => void;
  onOpen: () => void;
  patchPending: boolean;
}) {
  return (
    <Tr
      data-testid={`settings-playbook-row-${row.playbook_id}`}
      data-pinned={pinned ? "true" : "false"}
      className="border-b border-border-subtle hover:bg-recessed"
    >
      <Td>
        <button
          type="button"
          aria-label={pinned ? "Unpin default playbook" : "Pin as default"}
          aria-pressed={pinned}
          data-testid={`pin-playbook-${row.playbook_id}`}
          disabled={patchPending}
          onClick={onPin}
          className={[
            "h-6 w-6 rounded text-base leading-none",
            pinned
              ? "text-amber"
              : "text-text-tertiary hover:text-text-secondary",
            patchPending ? "opacity-50 cursor-not-allowed" : "",
          ].join(" ")}
        >
          {pinned ? "★" : "☆"}
        </button>
      </Td>
      <Td className="!align-top">
        <button
          type="button"
          onClick={onOpen}
          className="text-left text-sm font-medium text-text-primary hover:underline"
        >
          {row.name}
        </button>
        {row.description ? (
          <p className="mt-0.5 text-xs text-text-secondary">
            {row.description}
          </p>
        ) : null}
      </Td>
      <Td className="text-xs text-text-secondary">
        {row.is_system ? "System" : "Custom"}
      </Td>
      <Td className="text-xs text-text-secondary">
        <button
          type="button"
          onClick={onOpen}
          className="text-xs text-teal hover:underline"
        >
          View blocks
        </button>
      </Td>
    </Tr>
  );
}


function PlaybookBlocksPanel({
  playbookId, onClose,
}: {
  playbookId: string;
  onClose: () => void;
}): JSX.Element {
  const detail = usePlaybookDetailQuery(playbookId);
  const data = detail.data;

  return (
    <aside
      data-testid="settings-playbook-blocks-panel"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {data?.name ?? "Playbook"}
          </h3>
          {data?.description ? (
            <p className="mt-0.5 text-xs text-text-secondary">
              {data.description}
            </p>
          ) : null}
        </div>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      {detail.isLoading ? (
        <p className="mt-3 text-xs text-text-tertiary">Loading blocks…</p>
      ) : null}

      {data && data.activity_blocks.length === 0 ? (
        <p className="mt-3 text-xs text-text-tertiary">
          No activity blocks defined on this playbook.
        </p>
      ) : null}

      {data && data.activity_blocks.length > 0 ? (
        <ol
          data-testid="playbook-blocks-list"
          className="mt-3 flex flex-col gap-2 text-xs"
        >
          {data.activity_blocks.map((b) => (
            <li
              key={b.block_id}
              className="rounded border border-border-subtle bg-recessed px-3 py-2"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary">
                  {b.ordinal}. {b.name}
                </span>
                <span className="text-text-tertiary">
                  {b.target_offset_days_from_renewal} d
                </span>
              </div>
              <p className="mt-0.5 text-text-secondary">
                {b.activity_type} · {b.assigned_role}
                {b.required ? " · required" : ""}
              </p>
            </li>
          ))}
        </ol>
      ) : null}
    </aside>
  );
}
